import { computed, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, switchMap, throwError } from 'rxjs';
import { LogsType } from '../../../shared/constants/logs-type.enum';
import { UpdateClientDto } from '../../models/client/update-client.dto';
import { CreateClientDto } from '../../models/client/create-client.dto';
import { ClientDto, ClientParamsDto, PaginatedClientsDto } from '../../models/client/client.dto';
import { ClientError } from '../../errors/client-error.class';
import { ClientErrorType } from '../../../shared/constants/error-status.enum';
import { LoggerService } from '../logger/logger.service';
import { ClientApiService } from './client-api.service';
import { finalTimeForClient } from '../../helpers/helpers.class';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private readonly className = 'ClientService';
  private functionName = '';

  private readonly _currentClientSignal = signal<ClientDto>({} as ClientDto);
  readonly currentClientSignal = computed(() => this._currentClientSignal());

  constructor(
    private readonly logger: LoggerService,
    private readonly clientApiService: ClientApiService
  ) { }

  setCurrentClient(client: ClientDto): void {
    this._currentClientSignal.set(client);
  }

  /**
   * Retrieves clients based on the provided parameters.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.clientService.getClientByParams(params);
   *
   * // Use this in Interceptors, Guards and Functions
   * clientService.getClientByParams(params);
   * ```
   *
   * @param params - The parameters to filter and paginate the clients
   * @returns Observable<PaginatedClientsDto> - The clients and the pagination information
   */
  getClientByParams(params: ClientParamsDto): Observable<PaginatedClientsDto> {
    this.functionName = 'getClientByParams';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    return this.clientApiService.getApiClients(params).pipe(
      map((response: PaginatedClientsDto) => {
        if (!response || response.clients.length === 0) {
          this.logger.logMessage(LogsType.WARN, this.className, this.functionName, 'No clients were returned');
          return { clients: [], pagination: response?.pagination };
        }

        const clients = response.clients.map((client: ClientDto) => {
          const contract = client.contract ?? null;
          const isUnlimited = contract?.serviceIsDurationUnlimited ?? false;

          let daysUntilExpiration: number | null = null;
          if (!isUnlimited && contract?.expirationDate) {
            const msPerDay = 1000 * 60 * 60 * 24;
            daysUntilExpiration = Math.ceil(
              (new Date(contract.expirationDate).getTime() - Date.now()) / msPerDay
            );
          }

          return { ...client, lastContract: contract, daysUntilExpiration };
        });

        this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'Clients:', clients);
        return { clients, pagination: response.pagination };
      }),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * Retrieves a client by its name.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.clientService.getClientByName(clientName);
   *
   * // Use this in Interceptors, Guards and Functions
   * clientService.getClientByName(clientName);
   * ```
   *
   * @param clientName - The name of the client to retrieve (optional)
   * @returns Observable<PaginatedClientsDto> - The clients and the pagination information
   */
  getClientByName(clientName?: string): Observable<PaginatedClientsDto> {
    this.functionName = 'getClientByName';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const params: ClientParamsDto = {
      PartialName: clientName ?? ''
    };

    return this.getClientByParams(params).pipe(
      map((response: PaginatedClientsDto) => response),
      catchError((error) => throwError(() => error))
    );
  }

  getClientByEmail(clientEmail: string): Observable<PaginatedClientsDto> {
    this.functionName = 'getClientByEmail';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const params: ClientParamsDto = {
      Email: clientEmail
    };
    return this.getClientByParams(params).pipe(
      map((response: PaginatedClientsDto) => response),
      catchError((error) => throwError(() => error))
    );
  }

  getClientById(clientId: string): Observable<ClientDto> {
    this.functionName = 'getClientById';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    return this.clientApiService.getApiClientById(clientId).pipe(
      map((response: ClientDto) => response),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * Creates a new client and initializes related resources.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.clientService.createClient(client);
   *
   * // Use this in Interceptors, Guards and Functions
   * clientService.createClient(client);
   * ```
   *
   * @param client - The client data to create
   * @returns Observable<void> - Emits when the client is successfully created
   */
  createClient(client: CreateClientDto): Observable<void> {
    this.functionName = 'createClient';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    client.availableSupportTime = '00:00:00';

    return this.clientApiService.createApiClient(client).pipe(
      map((client: ClientDto) => {
        if (!client.clientId) {
          throw new ClientError(ClientErrorType.CLIENT_NOT_CREATED);
        }

        this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'Client created:', client.name);
        return;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates an existing client and its related resources.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.clientService.updateClient(clientId, client);
   *
   * // Use this in Interceptors, Guards and Functions
   * clientService.updateClient(clientId, client);
   * ```
   *
   * @param clientId - The ID of the client to update
   * @param client - The updated client data
   * @returns Observable<void> - Emits when the client is successfully updated
   */
  updateClient(clientId: string, client: UpdateClientDto): Observable<void> {
    this.functionName = 'updateClient';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    return this.clientApiService.updateApiClient(clientId, client).pipe(
      map(() => {
        this.setCurrentClient({ ...this.currentClientSignal(), ...client });
        this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'Client updated:', clientId);
        return;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  updateAvailableSupportTime(clientId: string, availableSupportTime: string, isAdd: boolean): Observable<void> {
    this.functionName = 'updateAvailableSupportTime';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    let updateClient: UpdateClientDto = {} as UpdateClientDto;

    return this.clientApiService.getApiClientById(clientId).pipe(
      switchMap((client: ClientDto) => {
        const updatedAvailableSupportTime = finalTimeForClient(client.availableSupportTime, availableSupportTime, isAdd);
        updateClient.availableSupportTime = updatedAvailableSupportTime ?? '';

        return this.updateClient(clientId, updateClient);
      }),
      map(() => {
        this.setCurrentClient({ ...this.currentClientSignal(), availableSupportTime: updateClient.availableSupportTime ?? '' });
        this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'Client available support time updated:', clientId);
        return;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Deletes a client and its related resources.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.clientService.deleteClient(clientId);
   *
   * // Use this in Interceptors, Guards and Functions
   * clientService.deleteClient(clientId);
   * ```
   *
   * @param clientId - The ID of the client to delete
   * @returns Observable<void> - Emits when the client is successfully deleted
   */
  deleteClient(clientId: string): Observable<void> {
    this.functionName = 'deleteClient';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    return this.clientApiService.deleteApiClient(clientId).pipe(
      map(() => {
        this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'Client deleted:', clientId);
        return;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
